import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../../context/AuthContext';
import { useLocationOptions } from '../../shared/useLocationOptions';
import { Field, Select, SectionHead, INP, INP_LOCKED } from '../../../reusable/formRecords/formFieldPrimitives';
import { MARKET_SCHEDULE_OPTIONS, MARKET_STATUS_OPTIONS, MARKET_TYPE_OPTIONS } from '../../../MarketTrad/marketTradOptions';
import { REGISTRY_TYPE_OPTIONS, matchRegistryType, registryCentralApi, registryStatusLabel, registryTypeLabel } from '../../../../api/registryCentralApi';

function ModalShell({ title, onClose, children, footer, wide = false }) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 px-4 py-8">
      <div className={`max-h-[92vh] w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-lg leading-none text-red-500 hover:bg-red-100">×</button>
        </div>
        <div className="space-y-6">{children}</div>
        <div className="mt-6 flex gap-3">{footer}</div>
      </div>
    </div>
  );
}

const Divider = () => <div className="h-px w-full bg-green-200" />;

function FooterButtons({ onCancel, onConfirm, confirmLabel, busy, disabled }) {
  return (
    <>
      <button type="button" onClick={onCancel} className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
      <button type="button" onClick={onConfirm} disabled={disabled || busy}
        className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? 'Please wait…' : confirmLabel}
      </button>
    </>
  );
}

// ── Create / edit one registry entry ─────────────────────────────────────────
export function CreateRegistryModal({ onClose, onSaved, entry = null }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    registry_type: entry?.registry_type ?? '', name: entry?.name ?? '',
    state: entry?.state ?? '', lga: entry?.lga ?? '', ward: entry?.ward ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions, lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(form.state, form.lga);
  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => (key === 'state' ? { ...prev, state: value, lga: '', ward: '' }
      : key === 'lga' ? { ...prev, lga: value, ward: '' } : { ...prev, [key]: value }));
  };
  const canSave = form.registry_type && form.name.trim().length >= 3 && form.state && form.lga;

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    setError('');
    try {
      if (entry) await registryCentralApi.update(entry.uuid, form);
      else await registryCentralApi.create(form, user);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.message ?? 'Failed to save the registry entry.');
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={entry ? 'Edit Registry' : 'Create New Registry'}
      onClose={onClose}
      footer={<FooterButtons onCancel={onClose} onConfirm={save} confirmLabel={entry ? 'Save Changes' : 'Create'} busy={busy} disabled={!canSave} />}
    >
      {error && <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">{error}</div>}
      <div className="space-y-4">
        <SectionHead title="Select" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Registry Type" required>
            <Select value={form.registry_type} onChange={set('registry_type')} options={REGISTRY_TYPE_OPTIONS} placeholder="Select type" />
          </Field>
          <Field label="Proposed Name" required hint="Minimum 3 characters">
            <input value={form.name} onChange={set('name')} placeholder="Type" className={INP} />
          </Field>
        </div>
      </div>
      <Divider />
      <div className="space-y-4">
        <SectionHead title="Geospatial" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="State" required>
            <Select value={form.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />
          </Field>
          <Field label="LGA" required>
            <Select value={form.lga} onChange={set('lga')} options={lgaOptions} disabled={!form.state || loadingLgas}
              placeholder={form.state ? (loadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'}
              className={form.state ? INP : INP_LOCKED} />
          </Field>
          <Field label="Ward">
            <Select value={form.ward} onChange={set('ward')} options={wardOptions} disabled={!form.lga || loadingWards}
              placeholder={form.lga ? (loadingWards ? 'Loading wards…' : 'Select ward') : 'Select LGA first'}
              className={form.lga ? INP : INP_LOCKED} />
          </Field>
        </div>
      </div>
      <Divider />
    </ModalShell>
  );
}

// ── Excel bulk upload ────────────────────────────────────────────────────────
// Captured columns (header row, any order, case-insensitive): Name, Registry
// Type, State, LGA, Ward. Status and Date Created are set by the system.
// Alternative header spellings are accepted so an exported sheet still maps.
const COLUMNS = [
  { key: 'name',          label: 'Name',          aliases: ['name', 'registry name', 'proposed name', 'entity name'] },
  { key: 'registry_type', label: 'Registry Type', aliases: ['registry type', 'type', 'entity type'] },
  { key: 'state',         label: 'State',         aliases: ['state'] },
  { key: 'lga',           label: 'LGA',           aliases: ['lga', 'local government', 'local government area'] },
  { key: 'ward',          label: 'Ward',          aliases: ['ward', 'ward / community', 'community'] },
];
const norm = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

// A row with no Registry Type uses the one picked in the form.
function mapSheetRows(rawRows, batchName, defaultType) {
  return rawRows
    .map((raw) => {
      const byHeader = Object.fromEntries(Object.entries(raw).map(([header, value]) => [norm(header), String(value ?? '').trim()]));
      const row = {};
      COLUMNS.forEach((col) => {
        const header = col.aliases.find((alias) => byHeader[alias] !== undefined && byHeader[alias] !== '');
        row[col.key] = header ? byHeader[header] : '';
      });
      return row;
    })
    .filter((row) => Object.values(row).some(Boolean))
    .map((row, index) => ({
      ...row,
      name: row.name || `${batchName} ${index + 1}`,
      typeText: row.registry_type,
      registry_type: row.registry_type ? matchRegistryType(row.registry_type) : defaultType,
    }));
}

function rowProblems(row, knownStates) {
  const problems = [];
  if (row.name.length < 3) problems.push('Name too short');
  if (!row.registry_type) problems.push(row.typeText ? 'Unknown registry type' : 'Registry type missing');
  if (!row.state) problems.push('State missing');
  else if (knownStates.size && !knownStates.has(norm(row.state))) problems.push('Unknown state');
  if (!row.lga) problems.push('LGA missing');
  return problems;
}

function downloadTemplate() {
  const sheet = XLSX.utils.aoa_to_sheet([
    COLUMNS.map((c) => c.label),
    ['Sabon Gari Market', 'Market', 'Kano', 'Fagge', 'Sabon Gari'],
  ]);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Registry');
  XLSX.writeFile(book, 'registry-upload-template.xlsx');
}

export function BulkUploadModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [registryType, setRegistryType] = useState('');
  const [batchName, setBatchName] = useState('');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions } = useLocationOptions('');
  const knownStates = new Set(stateOptions.map((o) => norm(o.value)));
  const ready = Boolean(registryType && batchName.trim());

  // A different registry type needs its own file, so clear what was read.
  useEffect(() => { setRows([]); setFileName(''); }, [registryType]);

  const readFile = async (file) => {
    if (!file) return;
    if (!ready) { setError('Select the Registry Type and enter the Proposed Name first.'); return; }
    if (!/\.(xlsx|xls)$/i.test(file.name)) { setError('Only .xlsx or .xls files are supported.'); return; }
    setError('');
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = book.Sheets[book.SheetNames[0]];
      const mapped = mapSheetRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }), batchName.trim(), registryType);
      if (mapped.length === 0) { setError('No rows found in the first sheet. Use the template columns: Name, Registry Type, State, LGA, Ward.'); return; }
      setFileName(file.name);
      setRows(mapped);
    } catch {
      setError('Could not read that file. Make sure it is a valid Excel workbook.');
    }
  };

  const problems = rows.map((row) => rowProblems(row, knownStates));
  const validRows = rows.filter((_, i) => problems[i].length === 0);

  const upload = async () => {
    if (!ready || validRows.length === 0 || busy) return;
    setBusy(true);
    try {
      await registryCentralApi.createMany(validRows, { batchName }, user);
      onSaved?.(validRows.length);
      onClose();
    } catch (e) {
      setError(e?.message ?? 'Upload failed.');
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Upload Registry Data"
      onClose={onClose}
      wide={rows.length > 0}
      footer={<FooterButtons onCancel={onClose} onConfirm={upload}
        confirmLabel={rows.length ? `Upload ${validRows.length} record${validRows.length === 1 ? '' : 's'}` : 'Upload'}
        busy={busy} disabled={!ready || validRows.length === 0} />}
    >
      {error && <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">{error}</div>}
      <div className="space-y-4">
        <SectionHead title="Select" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Registry Type" required hint="Used for any row whose Registry Type cell is empty.">
            <Select value={registryType} onChange={(e) => setRegistryType(e.target.value)} options={REGISTRY_TYPE_OPTIONS} placeholder="Select type" />
          </Field>
          <Field label="Proposed Name" required hint="Names this upload batch; also used for any row without a Name.">
            <input value={batchName} onChange={(e) => setBatchName(e.target.value)} placeholder="Type" className={INP} />
          </Field>
        </div>
      </div>
      <Divider />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-bold text-gray-900">Upload Registry Data <span className="text-red-500">*</span></label>
          <button type="button" onClick={downloadTemplate} className="text-xs font-semibold text-green-700 hover:text-green-800">Download template</button>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => (ready ? inputRef.current?.click() : setError('Select the Registry Type and enter the Proposed Name first.'))}
          onKeyDown={(e) => { if (e.key === 'Enter' && ready) inputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); readFile(e.dataTransfer.files?.[0]); }}
          className={`flex min-h-[110px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center text-xs font-semibold transition-colors ${
            !ready ? 'border-gray-200 bg-gray-50 text-gray-400'
              : dragging ? 'border-green-500 bg-green-100 text-green-700' : 'border-green-300 bg-green-50 text-green-600 hover:bg-green-100'}`}
        >
          {fileName ? (
            <>
              <span>{fileName}</span>
              <span className="mt-1 font-normal text-green-700">{rows.length} row{rows.length === 1 ? '' : 's'} read — click or drop to replace</span>
            </>
          ) : (
            <>
              <span>Drag &amp; drop your Excel file</span>
              <span>or [ Browse Files ]</span>
              <span>.xlsx or .xls supported</span>
              {!ready && <span className="mt-1 font-normal">Select the Registry Type and enter the Proposed Name first</span>}
            </>
          )}
        </div>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {rows.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Status and Date Created are set by the system · {validRows.length} of {rows.length} row{rows.length === 1 ? '' : 's'} ready to upload
            {rows.length !== validRows.length && ' — rows with problems are skipped'}
          </p>
          <div className="max-h-64 overflow-auto rounded-lg border border-gray-100">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-gray-50 text-[11px] font-semibold text-gray-600">
                <tr>
                  {COLUMNS.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}
                  <th className="px-3 py-2">Check</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    {COLUMNS.map((c) => (
                      <td key={c.key} className="px-3 py-2 text-gray-700">
                        {(c.key === 'registry_type' ? (row.registry_type ? registryTypeLabel(row.registry_type) : row.typeText) : row[c.key]) || '—'}
                      </td>
                    ))}
                    <td className={`px-3 py-2 font-semibold ${problems[i].length ? 'text-red-600' : 'text-green-600'}`}>
                      {problems[i].length ? problems[i].join(', ') : 'OK'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Divider />
    </ModalShell>
  );
}

// ── View details ─────────────────────────────────────────────────────────────
const optionLabel = (options, value) => options.find((o) => o.value === value)?.label ?? value;

export function RegistryDetailsModal({ entry, onClose, onValidate, onReject, onComplete, onOpenRecord, initialRejecting = false }) {
  const [rejecting, setRejecting] = useState(initialRejecting);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };
  const rows = [
    ['Registry Name', entry.name], ['Registry Type', registryTypeLabel(entry.registry_type)],
    ['State', entry.state], ['LGA', entry.lga], ['Ward', entry.ward],
    ...(entry.market_type ? [['Market Type', optionLabel(MARKET_TYPE_OPTIONS, entry.market_type)]] : []),
    ...(entry.market_status ? [['Market Status', optionLabel(MARKET_STATUS_OPTIONS, entry.market_status)]] : []),
    ...(entry.market_schedule ? [['Market Schedule', optionLabel(MARKET_SCHEDULE_OPTIONS, entry.market_schedule)]] : []),
    ['Registry Status', registryStatusLabel(entry.status)],
    ['Source', { bulk_upload: `Bulk upload — ${entry.batch_name}`, form_proposal: 'Proposed from a module form', module_registry: 'Created in Market Registry' }[entry.source] ?? 'Created in Registry Central'],
    ['Created By', [entry.created_by, entry.created_by_role && `(${entry.created_by_role})`].filter(Boolean).join(' ')], ['Date Created', String(entry.created_at ?? '').slice(0, 10)],
    ...(entry.rejection_notes ? [['Rejection Notes', entry.rejection_notes]] : []),
    ...(entry.linked_record_uuid ? [['Linked Record', 'Completed in Market Registry']] : []),
  ];
  const btn = 'h-11 flex-1 rounded-full text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40';
  const closeBtn = <button type="button" onClick={onClose} className={`${btn} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}>Close</button>;
  let footer = closeBtn;
  if (entry.status === 'pending_validation' && onValidate && rejecting) {
    footer = (
      <>
        <button type="button" onClick={() => setRejecting(false)} className={`${btn} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}>Back</button>
        <button type="button" disabled={busy || notes.trim().length < 3} onClick={() => run(() => onReject(notes.trim()))} className={`${btn} bg-red-600 text-white hover:bg-red-700`}>Confirm Reject</button>
      </>
    );
  } else if (entry.status === 'pending_validation' && onValidate) {
    footer = (
      <>
        {closeBtn}
        <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={`${btn} border border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}>Reject</button>
        <button type="button" disabled={busy} onClick={() => run(onValidate)} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>Validate</button>
      </>
    );
  } else if (entry.status === 'pending_completion' && onComplete) {
    footer = <>{closeBtn}<button type="button" onClick={onComplete} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>Complete in Market Registry</button></>;
  } else if (onOpenRecord) {
    footer = <>{closeBtn}<button type="button" onClick={onOpenRecord} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>Open in Market Registry</button></>;
  }
  return (
    <ModalShell title="Registry Details" onClose={onClose} footer={footer}>
      <div>
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between border-b border-gray-50 py-2 last:border-0">
            <span className="text-xs font-semibold text-gray-500">{label}</span>
            <span className="text-right text-sm text-gray-900">{value || '—'}</span>
          </div>
        ))}
        {entry.status === 'pending_completion' && (
          <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">Validated — the remaining details are captured in Market Registry; saving there makes this entry Active.</p>
        )}
        {rejecting && (
          <div className="mt-4">
            <label className="mb-1.5 block text-[13px] font-bold text-gray-900">Rejection Notes <span className="text-red-500">*</span></label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why is this proposal rejected? (min 3 characters)"
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300" />
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// ── Confirm (Complete / Archive / Restore …) ─────────────────────────────────
export function ConfirmModal({ title, message, confirmLabel, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="mb-2 text-base font-bold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={onConfirm} className="h-10 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
